import API, { ensurePrefixed } from 'utils/api';
import { onSuccess, onError } from 'utils/handlers';
import getFormattedValues, { formatReceived, hasFiles } from 'utils/formatValues';
import { getPredio } from 'api/catastro/predio';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import padronToString from 'utils/padronesToString';
import { getContribuyente } from './contribuyentes';

/**
 * URI base para las operaciones de expedientes de anuncios
 * @type {string}
 */
export const baseURI = '/empresas/expedientes-de-anuncios/';
const files = ['documentacion_de_evidencia'];
/**
 * Obtiene los datos de contribuyentes y predios para una lista de expedientes
 * @param {Array<Object>} expedientes - Lista de expedientes
 * @returns {Promise<{contribuyentes: Array<Object>, predios: Array<Object>}>}
 */
const obtenerDatosRelacionados = async (expedientes) => {
  const contribuyentesIds = [
    ...new Set(expedientes.map((item) => item.contribuyente).filter((id) => id !== null)),
  ];
  const prediosIds = [
    ...new Set(expedientes.map((item) => item.predio).filter((id) => id !== null)),
  ];

  const promisesContribuyentes = contribuyentesIds.map((id) => getContribuyente(id));

  const promisesPredios = prediosIds.map((id) => getPredio(id));

  const [contribuyentes, predios] = await Promise.all([
    Promise.all(promisesContribuyentes),
    Promise.all(promisesPredios),
  ]);

  return { contribuyentes, predios };
};

/**
 * Combina los datos de expedientes con sus contribuyentes y predios relacionados
 * @param {Array<Object>} expedientes - Lista de expedientes
 * @param {Array<Object>} contribuyentes - Lista de contribuyentes
 * @param {Array<Object>} predios - Lista de predios
 * @returns {Array<Object>}
 */
const combinarDatosRelacionados = (expedientes, contribuyentes, predios) => expedientes
  .map((item) => ({
    ...item,
    contribuyente: contribuyentes.find((c) => c?.id === item.contribuyente),
    predio: predios.find((p) => p?.id === item.predio),
    empresa__nombre_comercial: item.empresa
      ? padronToString(item.empresa, padronesEnum.EMPRESA) : null,
  }));

/**
 * Obtiene la lista de expedientes de anuncios
 * @param {Object} [params=null] - Parámetros de filtrado y paginación
 * @returns {Promise<{
 *   results: Array<Object>,
 *   count: number,
 *   next: string|null,
 *   previous: string|null
 * }>}
 */
export const getExpedientesDeAnuncios = async (params = null) => {
  try {
    const response = await API.get(baseURI, { params });
    const { contribuyentes, predios } = await obtenerDatosRelacionados(response.data.results);
    const results = combinarDatosRelacionados(response.data.results, contribuyentes, predios);
    return formatReceived({ ...response.data, results });
  } catch (err) {
    onError(err);
    return { results: [] };
  }
};

/**
 * Obtiene un expediente de anuncios específico por su ID
 * @param {number} id - ID del expediente
 * @returns {Promise<Object|null>}
 */
export const getExpedienteDeAnuncios = async (id) => {
  try {
    const response = await API.get(`${baseURI}${id}`);
    const { contribuyentes, predios } = await obtenerDatosRelacionados([response.data]);
    const [result] = combinarDatosRelacionados([response.data], contribuyentes, predios);

    return formatReceived({
      ...response.data,
      ...result,
    });
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * Crea un nuevo expediente de anuncios
 * @param {Object} values - Datos del expediente a crear
 * @returns {Promise<Object|boolean>}
 */
export const postExpedienteDeAnuncios = async (_values, accion = 'ANUNCIO') => {
  try {
    const values = getFormattedValues(_values);
    const accionInf = ensurePrefixed('CREAR_', accion);
    const config = hasFiles(values, files) ? {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    } : {};
    const formattedValues = getFormattedValues(values, {
      files,
      formData: config.headers,
      clean: false,
    });
    const response = await API.post(baseURI, formattedValues, {
      ...config,
      accionInf,
      idPadron: padronesEnum.EXPEDIENTE_DE_ANUNCIO,
      clave: formattedValues.clave,
    });
    onSuccess(response, 'Agregado correctamente');
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * Actualiza un expediente de anuncios existente
 * @param {number} id - ID del expediente a actualizar
 * @param {Object} values - Datos actualizados del expediente
 * @returns {Promise<Object|boolean>}
 */
export const patchExpedienteDeAnuncios = async (id, _values, accion = 'ANUNCIO') => {
  try {
    const values = getFormattedValues(_values, { clean: false });
    const accionInf = ensurePrefixed('EDITAR_', accion);
    const config = hasFiles(values, files) ? {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    } : {};
    const formattedValues = getFormattedValues(values, {
      files,
      formData: config.headers,
      clean: false,
    });
    const response = await API.patch(`${baseURI}${id}`, formattedValues, {
      ...config,
      accionInf,
      idPadron: padronesEnum.EXPEDIENTE_DE_ANUNCIO,
      clave: formattedValues.clave,
    });
    onSuccess(response, 'Editado correctamente');
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * Elimina un expediente de anuncios
 * @param {number} id - ID del expediente a eliminar
 * @returns {Promise<Object|boolean>}
 */
export const deleteExpedienteDeAnuncios = async (id) => {
  try {
    const response = await API.delete(`${baseURI}${id}/`);
    const { contribuyentes, predios } = await obtenerDatosRelacionados([response.data]);
    const [result] = combinarDatosRelacionados([response.data], contribuyentes, predios);

    onSuccess(response, 'Eliminado correctamente');
    return formatReceived({
      ...response.data,
      ...result,
    });
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * Obtiene los expedientes de anuncios para la caja
 * @param {Object} [params=null] - Parámetros de filtrado y paginación
 * @returns {Promise<Array<Object>|{count: number, results: Array<Object>}>}
 */
export const getExpedienteDeAnunciosCaja = async (params = null) => {
  try {
    const response = await API.get('/empresas/expedientes-de-anuncios-caja/', { params });

    // Si hay parámetros de paginación, retornamos la estructura paginada
    if (params?.page) {
      const data = response.data?.results ?? [];
      const { contribuyentes, predios } = await obtenerDatosRelacionados(data);
      const results = combinarDatosRelacionados(data, contribuyentes, predios);
      return {
        ...response.data,
        results,
      };
    }

    // Si no hay paginación, retornamos solo la lista de resultados
    let data = response.data?.results ?? response?.data;
    if (!Array.isArray(data)) {
      data = data ? [data] : [];
    }
    const { contribuyentes, predios } = await obtenerDatosRelacionados(data);
    return combinarDatosRelacionados(data, contribuyentes, predios);
  } catch (err) {
    onError(err);
    return params?.page ? { count: 0, results: [] } : [];
  }
};
